import { redirect } from '@tanstack/react-router';
import { chainServiceKeys } from '@/lib/services/chain.service';
import { ensureMyEffectivePermissions, type PermissionGuardContext } from './permission-guard';

type ChainAggregatedCounts = { activeMembers?: number } | undefined;

/**
 * Route-level gate for Member Portal authoring (`/member-portal/manage/*`).
 *
 * Mirrors the `canAuthor` derivation in `useMemberPortalAccess`:
 *   canAuthor = (intranet.author && isParent) || isChainAccess
 *
 * Why a custom guard and not the plain permission guard?
 * A member-company admin (e.g. Kari at "Trondheim Elektro") may hold roles on
 * her own firm, but authoring lives on the chain parent. This guard catches her
 * at the route boundary and redirects to the viewer surface where she belongs.
 *
 * `isChainAccess` is derived client-side from the same signals
 * `useChainAccess` uses: when the selected company is NOT in the user's direct
 * memberships, they can only be there via chain access.
 *
 * The permission check reads through the shared React Query cache (matching
 * `usePermissions`); an unresolved fetch is permissive — the backend authoring
 * controllers all enforce `intranet.author` server-side.
 *
 * `isParent` requires the chain-counts query; this guard reads from the React
 * Query cache (no network blocking). If the count isn't cached yet, the
 * guard is permissive — the page will rehydrate the cache via the existing
 * `useMemberPortalAccess` hook and the page guard handles the edge case.
 */
export const requireMemberPortalAuthor = async (context: PermissionGuardContext, redirectTo = '/member-portal') => {
	const { globalStore, queryClient } = context;
	const state = globalStore.getState();
	const currentCompanyId = state.currentCompany?.id;

	if (!currentCompanyId) {
		throw redirect({ to: redirectTo });
	}

	// `isChainAccess` short-circuits everything — HQ admins observing a member
	// always elevate to author.
	const hasDirectMembership = state.companies.some((c) => c.id === currentCompanyId);
	const isChainAccess = !hasDirectMembership;
	if (isChainAccess) return;

	// Otherwise require the authoring permission on the current company.
	const effective = await ensureMyEffectivePermissions(context);
	if (effective && !effective.isBypass && !effective.permissions.includes('intranet.author')) {
		throw redirect({ to: redirectTo });
	}

	// And require the company to BE a chain parent (have at least one active
	// member). Read from cache; if absent we let the route render and let the
	// page-level hook bounce later if needed.
	const counts = queryClient.getQueryData<ChainAggregatedCounts>([...chainServiceKeys.aggregatedCounts({})]);
	if (counts !== undefined && (counts?.activeMembers ?? 0) <= 0) {
		throw redirect({ to: redirectTo });
	}
};
